import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button } from './ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BroadcastProgress {
  current: number;
  total: number;
  percentage: number;
  status: 'initializing' | 'counting' | 'processing' | 'completed' | 'completed_with_errors' | 'error';
  message: string;
  stats?: {
    total: number;
    processed: number;
    succeeded: number;
    skipped: number;
    failed: number;
    errors: string[];
  };
}

interface PreviewResponse {
  count: number;
}

interface BroadcastResponse {
  jobId: string;
  message: string;
}

interface LendersResponse {
  lenders: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(['completed', 'completed_with_errors', 'error']);

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('en-GB').format(n);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MemoBroadcast: React.FC = () => {
  const { apiRequest } = useAuth();

  // Form state
  const [memoTitle, setMemoTitle] = useState('');
  const [memoContent, setMemoContent] = useState('');
  const [selectedLender, setSelectedLender] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [schemeFilter, setSchemeFilter] = useState('');

  // Lenders
  const [lenders, setLenders] = useState<string[]>([]);
  const [loadingLenders, setLoadingLenders] = useState(false);

  // Preview
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Job / progress
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<BroadcastProgress | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // General error
  const [error, setError] = useState('');

  // Polling ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ------------------------------------------------------------------
  // Fetch lenders on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    const loadLenders = async () => {
      setLoadingLenders(true);
      try {
        const data = await apiRequest<LendersResponse>({
          url: '/ui/admin/memo-broadcast/lenders',
          method: 'GET',
        });
        setLenders(data.lenders ?? []);
      } catch (err: any) {
        console.error('Failed to load lenders:', err);
        setError(err.message || 'Failed to load lenders');
      } finally {
        setLoadingLenders(false);
      }
    };
    loadLenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Polling for progress
  // ------------------------------------------------------------------
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!jobId) return;

    const fetchProgress = async () => {
      try {
        const data = await apiRequest<BroadcastProgress>({
          url: `/ui/admin/memo-broadcast/progress/${jobId}`,
          method: 'GET',
        });
        setProgress(data);

        if (TERMINAL_STATUSES.has(data.status)) {
          stopPolling();
        }
      } catch (err: any) {
        console.error('Error polling progress:', err);
        stopPolling();
        setProgress({
          current: 0,
          total: 0,
          percentage: 0,
          status: 'error',
          message: 'Broadcast job not found. The server may have restarted. Please start a new broadcast.',
        });
      }
    };

    // Fetch immediately, then every 2 seconds
    fetchProgress();
    pollRef.current = setInterval(fetchProgress, 2000);

    return stopPolling;
  }, [jobId, apiRequest, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return stopPolling;
  }, [stopPolling]);

  // ------------------------------------------------------------------
  // Preview
  // ------------------------------------------------------------------
  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewCount(null);
    setError('');
    try {
      const data = await apiRequest<PreviewResponse>({
        url: '/ui/admin/memo-broadcast/preview',
        method: 'POST',
        data: {
          lenderName: selectedLender || null,
          testMode,
          schemeFilter: schemeFilter || null,
        },
      });
      setPreviewCount(data.count);
    } catch (err: any) {
      console.error('Preview error:', err);
      setError(err.message || 'Failed to load preview count');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Submit broadcast
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!memoTitle.trim() || !memoContent.trim()) return;

    const schemeLabel = schemeFilter === 'scheme_1' ? ' (Scheme 1 only)' :
      schemeFilter === 'scheme_2' ? ' (Scheme 2 only)' :
      schemeFilter === 'both' ? ' (Both Schemes)' : '';

    const confirmed = window.confirm(
      testMode
        ? 'Send this memo to test claims only?'
        : selectedLender
          ? `Broadcast this memo to all live claims for "${selectedLender}"${schemeLabel}?`
          : `Broadcast this memo to ALL live claims${schemeLabel}? This cannot be undone.`
    );
    if (!confirmed) return;

    setIsSubmitting(true);
    setError('');
    setProgress(null);
    setJobId(null);

    try {
      const data = await apiRequest<BroadcastResponse>({
        url: '/ui/admin/memo-broadcast',
        method: 'POST',
        data: {
          title: memoTitle.trim(),
          content: memoContent.trim(),
          lenderName: selectedLender || null,
          testMode,
          schemeFilter: schemeFilter || null,
        },
      });
      setJobId(data.jobId);
    } catch (err: any) {
      console.error('Broadcast error:', err);
      setError(err.message || 'Failed to start broadcast');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Reset form after broadcast completes
  // ------------------------------------------------------------------
  const handleReset = () => {
    setJobId(null);
    setProgress(null);
    setMemoTitle('');
    setMemoContent('');
    setSelectedLender('');
    setTestMode(false);
    setSchemeFilter('');
    setPreviewCount(null);
    setError('');
  };

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------
  const canSubmit = memoTitle.trim().length > 0 && memoContent.trim().length > 0 && !isSubmitting && !jobId;
  const isTerminal = progress && TERMINAL_STATUSES.has(progress.status);
  const lenderTarget = selectedLender || 'all';

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <PageHeader title="Memo Broadcast" description="Send custom memo correspondence to live claims" />

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
          </div>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setError('')}
            aria-label="Dismiss error"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </Button>
        </div>
      )}

      {/* Memo Title */}
      <Card className="mb-6">
        <label htmlFor="memo-title" className="block text-sm font-medium text-gray-700 mb-1">
          Memo Title
        </label>
        <input
          id="memo-title"
          type="text"
          value={memoTitle}
          onChange={(e) => setMemoTitle(e.target.value)}
          placeholder="Enter a title for this memo..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          disabled={!!jobId}
        />
      </Card>

      {/* Memo Content + Preview */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: textarea */}
          <div>
            <label htmlFor="memo-content" className="block text-sm font-medium text-gray-700 mb-1">
              Memo Content
            </label>
            <textarea
              id="memo-content"
              value={memoContent}
              onChange={(e) => setMemoContent(e.target.value)}
              placeholder="Write the memo content here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-y"
              style={{ minHeight: '200px' }}
              disabled={!!jobId}
            />
          </div>

          {/* Right: live preview */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Preview</span>
            <div
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm overflow-auto"
              style={{ minHeight: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {memoContent.trim() ? (
                memoContent
              ) : (
                <span className="text-gray-400 italic">Preview will appear here...</span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Target Selection */}
      <Card title="Target Selection" className="mb-6">
        <div className="space-y-3">
          {/* All vs Specific */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                value=""
                checked={lenderTarget === 'all'}
                onChange={() => setSelectedLender('')}
                disabled={testMode || !!jobId}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">All Live Claims</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                value="specific"
                checked={lenderTarget !== 'all'}
                onChange={() => setSelectedLender(lenders[0] ?? '')}
                disabled={testMode || !!jobId || lenders.length === 0}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                Specific Lender{loadingLenders ? ' (loading...)' : lenders.length === 0 && !loadingLenders ? ' (none available)' : ''}
              </span>
            </label>

            {lenderTarget !== 'all' && !testMode && (
              <select
                value={selectedLender}
                onChange={(e) => setSelectedLender(e.target.value)}
                disabled={loadingLenders || !!jobId}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 max-w-xs"
              >
                {loadingLenders ? (
                  <option>Loading lenders...</option>
                ) : (
                  lenders.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))
                )}
              </select>
            )}
          </div>

          {/* Test mode */}
          <div className="pt-2 border-t border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => {
                  setTestMode(e.target.checked);
                  if (e.target.checked) {
                    setSelectedLender('');
                  }
                  setPreviewCount(null);
                }}
                disabled={!!jobId}
                className="rounded text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Test Mode</span>
              <span className="text-xs text-gray-500">(Only sends to CG174215.001 and CG154633.001)</span>
            </label>
            {testMode && (
              <p className="mt-1 ml-6 text-xs text-amber-600 font-medium">
                Test mode active -- lender selection is disabled.
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Redress Scheme Filter */}
      <Card title="Redress Scheme Filter" className="mb-6">
        <p className="text-xs text-gray-500 mb-3">
          Filter by FCA redress scheme based on credit agreement start date
        </p>

        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="schemeFilter"
              value=""
              checked={schemeFilter === ''}
              onChange={() => { setSchemeFilter(''); setPreviewCount(null); }}
              disabled={!!jobId}
              className="text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">All Claims (no scheme filter)</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="schemeFilter"
              value="scheme_1"
              checked={schemeFilter === 'scheme_1'}
              onChange={() => { setSchemeFilter('scheme_1'); setPreviewCount(null); }}
              disabled={!!jobId}
              className="text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">
              Scheme 1 only
              <span className="text-xs text-gray-500 ml-1">(6 Apr 2007 – 31 Mar 2014)</span>
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="schemeFilter"
              value="scheme_2"
              checked={schemeFilter === 'scheme_2'}
              onChange={() => { setSchemeFilter('scheme_2'); setPreviewCount(null); }}
              disabled={!!jobId}
              className="text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">
              Scheme 2 only
              <span className="text-xs text-gray-500 ml-1">(1 Apr 2014 – 1 Nov 2024)</span>
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="schemeFilter"
              value="both"
              checked={schemeFilter === 'both'}
              onChange={() => { setSchemeFilter('both'); setPreviewCount(null); }}
              disabled={!!jobId}
              className="text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">
              Both Schemes
              <span className="text-xs text-gray-500 ml-1">(eligible claims only, excludes before/after/no-date)</span>
            </span>
          </label>
        </div>
      </Card>

      {/* Actions: Preview + Submit */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Preview */}
          <Button
            onClick={handlePreview}
            disabled={previewLoading || !!jobId}
            variant="secondary"
          >
            {previewLoading ? 'Checking...' : 'Preview Count'}
          </Button>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant="primary"
          >
            {isSubmitting ? 'Starting...' : 'Broadcast Memo'}
          </Button>

          {/* Preview count result */}
          {previewCount !== null && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                This will add a memo to <strong>{formatNumber(previewCount)}</strong> claim{previewCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Progress */}
      {(jobId || progress) && (
        <Card title="Broadcast Progress" className="mb-6">

          {progress ? (
            <div className="space-y-4">
              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>
                    {formatNumber(progress.current)} / {formatNumber(progress.total)}
                  </span>
                  <span>{Math.round(progress.percentage)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${
                      progress.status === 'error'
                        ? 'bg-red-500'
                        : progress.status === 'completed_with_errors'
                          ? 'bg-amber-500'
                          : progress.status === 'completed'
                            ? 'bg-green-500'
                            : 'bg-teal-500'
                    }`}
                    style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                  />
                </div>
              </div>

              {/* Status message */}
              <p className="text-sm text-gray-600">{progress.message}</p>

              {/* Terminal state results */}
              {isTerminal && (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    progress.status === 'completed'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : progress.status === 'completed_with_errors'
                        ? 'bg-amber-50 border border-amber-200 text-amber-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                >
                  <p className="font-medium">
                    {progress.status === 'completed' && 'Broadcast completed successfully.'}
                    {progress.status === 'completed_with_errors' && 'Broadcast completed with some errors.'}
                    {progress.status === 'error' && 'Broadcast failed.'}
                  </p>
                  <p className="mt-1">
                    Processed {formatNumber(progress.current)} of {formatNumber(progress.total)} claims.
                    {progress.stats && progress.stats.skipped > 0 && (
                      <span className="ml-1">({formatNumber(progress.stats.skipped)} already had this memo)</span>
                    )}
                  </p>

                  {/* Show errors if any */}
                  {progress.stats?.errors && progress.stats.errors.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium underline">
                        View {progress.stats.errors.length} error{progress.stats.errors.length !== 1 ? 's' : ''}
                      </summary>
                      <ul className="mt-2 space-y-1 text-xs max-h-40 overflow-y-auto">
                        {progress.stats.errors.map((e, i) => (
                          <li key={i} className="font-mono">{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {/* Nav-away note while running */}
              {!isTerminal && (
                <p className="text-xs text-gray-400 italic">
                  You can safely navigate away from this page. The broadcast will continue in the background.
                </p>
              )}

              {/* Reset button after completion */}
              {isTerminal && (
                <Button
                  onClick={handleReset}
                  variant="primary"
                >
                  New Broadcast
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 text-teal-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Initializing broadcast...
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default MemoBroadcast;
